import { Component, Input, OnInit, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DonationCardService } from '../../../services/donation-card.service';
import { FECComplianceUpdateViewModel } from '../../../models/DonationCardModels';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { MainModel } from '../../../../global/model/MainModel';

@Component({
  selector: 'app-donation-fec-compliance-tab',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    CardModule,
    InputSwitchModule,
    ButtonModule,
    DropdownModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextareaModule,
    InputTextModule,
    ToastModule
  ],
  templateUrl: './donation-fec-compliance-tab.component.html',
  styleUrl: './donation-fec-compliance-tab.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [MessageService]
})
export class DonationFecComplianceTabComponent implements OnInit {
  @Input() donationId!: number;
  @Input() splitId!: number;
  @Input() isReadOnlyMode: boolean = false;
  @Output() fecInfoRetrieved = new EventEmitter<FECComplianceUpdateViewModel>();
  fecInfo?: FECComplianceUpdateViewModel;

  // Modal and Form Variables
  isEditModalVisible = false;
  isEditDataLoading = false;
  editForm!: FormGroup;
  fecLineNumberOptions = [
    { text: 'Option 1', value: 1 },
    { text: 'Option 2', value: 2 }
  ];
  campaignId!: number;  

  constructor(
    private service: DonationCardService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private mainModel: MainModel
  ) { }

  ngOnInit() {
    const c: any = this.mainModel.selectedCampaign();
    this.campaignId = parseInt(c?.campaignId);
    if (isNaN(this.campaignId)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Campaign ID',
        detail: 'The selected campaign ID is invalid. Please select a valid campaign.',
        life: 5000
      });
      return;
    }
    this.service.getFecCompliance(this.donationId, this.splitId, this.campaignId).subscribe({
      next: (data) => {
        this.fecInfo = data;
        this.fecInfoRetrieved.emit(this.fecInfo);
        this.fecLineNumberOptions = data?.fecLineNumbers?.map((item: any) => ({
          text: item.text,
          value: item.value
        }));
        this.initializeForm();
      },
      error: () => {
        this.fecInfo = undefined; 
        this.fecLineNumberOptions = [];
        this.initializeForm();
      }
    });

    
    if (!this.editForm) {
      this.initializeForm();
    }
  }

  initializeForm() {
    // Ensure `editForm` is always initialized
    this.editForm = this.fb.group({
      fecLineNumberId: [this.fecInfo?.fecLineNumberId?.toString() || null, Validators.required],
      fecDescription: [this.fecInfo?.fecDescription || '', Validators.required],
      memoText: [this.fecInfo?.memoText || ''],
      isMemo: [this.fecInfo?.isMemo || false],
      fecLineNumber: [this.fecInfo?.fecLineNumber || ''] 
    });

    
    this.editForm.get('fecLineNumberId')?.valueChanges.subscribe((selectedValue) => {
      const selectedOption = this.fecLineNumberOptions.find(option => option.value === selectedValue);
      if (selectedOption) {
        this.editForm.patchValue({
          fecLineNumber: selectedOption.text
        });
      }
    });
  }

  isInvalid(controlName: string): boolean {
    if (!this.editForm) return false; 
    const control = this.editForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  onEditClick() {
    this.isEditModalVisible = true; 
    this.initializeForm(); 
  }

  onEditCancel() {
    this.isEditModalVisible = false;
    this.editForm.reset();
  }

  onEditSubmit() {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.editForm.valid) {
      const updatedData: FECComplianceUpdateViewModel = this.editForm.value;
      this.isEditDataLoading = true;
      this.service.saveFecInfo(this.donationId, this.splitId, updatedData).subscribe({
        next: () => {
          this.isEditDataLoading = false;
          this.isEditModalVisible = false;

          this.fecInfo = {
            ...this.fecInfo,
            fecLineNumberId: updatedData.fecLineNumberId,
            fecDescription: updatedData.fecDescription,
            memoText: updatedData.memoText,
            isMemo: updatedData.isMemo,
            fecLineNumber: updatedData.fecLineNumber
          };

          this.fecInfoRetrieved.emit(this.fecInfo);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'FEC compliance information updated successfully.'
          });
        },
        error: () => {
          this.isEditDataLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update FEC compliance information. Please try again.'
          });
        }
      });
    } else {
      this.editForm.markAllAsTouched();
    }
  }
}
